import {FC} from 'react'

import {Text} from '@/components/common/content/Text'
import {getClassAssistClassContent, getSpecimenDescriptionByClass} from '@/content/classAssist'
import {Link, Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {getClassLabel} from '@/utils/class-assist/helpers'
import {Locale} from '@/utils/constants'
import {getGSClassNamesByLanguage} from '@/utils/goods-and-services/classNames'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {ClassAssistOverviewLayout} from '../layout/ClassAssistOverviewLayout'
import {ClassAssistClassOverviewSection} from '../layout/ClassAssistOverviewSection'

type ClassAssistClassOverviewProps = {
  classCode: string
  description: string
  associatedClassCodes: string[]
  relatedSectors: {
    sectorName: string
    sectorSlug: string
    industrySlug: string
    industryName: string
    items: {name: string; slug: string}[]
    classCode: string
  }[]
  items: {slug: string; name: string}[]
  examples: {slug: string; name: string}[]
}

export const ClassAssistClassOverview: FC<
  React.PropsWithChildren<ClassAssistClassOverviewProps>
> = ({classCode, description, associatedClassCodes, relatedSectors, items, examples}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {
    classTitle,
    whatIsIncludedTitle,
    examplesTitle,
    recommendedTitle,
    associatedTitle,
    associatedDescription,
    specimenTitle,
  } = getClassAssistClassContent(classCode)

  const classNames = getGSClassNamesByLanguage(language)
  const specimenDescription = getSpecimenDescriptionByClass(classCode)
  const isEnLanguage = language === Locale.en

  return (
    <>
      <ClassAssistOverviewLayout title={`${t(classTitle)} ${classCode}`} description={description}>
        {items.length > 0 && (
          <ClassAssistClassOverviewSection title={t(whatIsIncludedTitle, {classCode})}>
            <ul className="columns">
              {items.map(({name, slug}) => (
                <li key={slug} className="item">
                  <Text>
                    <Link
                      href={routeTo.classAssist.class(classCode, slug)}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a className="underline">{name}</a>
                    </Link>
                  </Text>
                </li>
              ))}
            </ul>
          </ClassAssistClassOverviewSection>
        )}
        {isEnLanguage && examples.length > 0 && (
          <ClassAssistClassOverviewSection title={t(examplesTitle, {classCode})}>
            <ul>
              {examples.map(({name, slug}) => (
                <li key={name} className="item">
                  <Text>
                    <Link
                      href={routeTo.trademarkHallOfFame.slug(slug)}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a target="_blank" rel="noreferrer" className="underline">
                        {name}
                      </a>
                    </Link>
                  </Text>
                </li>
              ))}
            </ul>
          </ClassAssistClassOverviewSection>
        )}
        {relatedSectors.length > 0 && (
          <ClassAssistClassOverviewSection title={t(recommendedTitle, {classCode})}>
            <ul>
              {relatedSectors.map(({industryName, sectorName, industrySlug, sectorSlug}) => (
                <li key={`${industryName}-${sectorName}`} className="item">
                  <Text>
                    <Link
                      href={routeTo.classAssist.industry(industrySlug, sectorSlug)}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a className="underline">{`${industryName} - ${sectorName}`}</a>
                    </Link>
                  </Text>
                </li>
              ))}
            </ul>
          </ClassAssistClassOverviewSection>
        )}
        {associatedClassCodes.length > 0 && (
          <ClassAssistClassOverviewSection
            title={t(associatedTitle, {classCode})}
            description={t(associatedDescription)}
          >
            <ul>
              {associatedClassCodes.map((classCode) => (
                <li key={classCode} className="item">
                  <Text>
                    <Link
                      href={routeTo.classAssist.class(classCode)}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a className="underline">{getClassLabel(t, classNames, classCode)}</a>
                    </Link>
                  </Text>
                </li>
              ))}
            </ul>
          </ClassAssistClassOverviewSection>
        )}
        {isEnLanguage && (
          <ClassAssistClassOverviewSection
            title={t(specimenTitle, {classCode})}
            description={
              <Trans i18nKey={specimenDescription} values={{classCode}}>
                text
                <Link
                  href={routeTo.specimenGuide.class(classCode)}
                  prefetch={false}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  link
                </Link>
              </Trans>
            }
          />
        )}
      </ClassAssistOverviewLayout>

      <style jsx>{`
        .item {
          list-style-type: disc;
          margin-left: ${theme.spacing[4]};
        }

        .item + .item {
          margin-top: ${theme.spacing[2]};
        }

        .columns {
          column-count: 2;
          column-gap: ${theme.spacing[7]};
        }

        .columns .item {
          break-inside: avoid;
          margin-top: 0;
          padding-bottom: ${theme.spacing[2]};
        }

        @media ${MOBILE_SCREENSIZE} {
          .columns {
            column-count: 1;
          }
        }
      `}</style>
    </>
  )
}
